import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { RFValue } from 'react-native-responsive-fontsize';
import Modal from 'react-native-modal';
import { Linking } from 'react-native';


import { Button } from '../Forms/Buttons/Button';
import { Input } from '../Forms/Inputs/Input';
import BannerModal from '../../../assets/images/clube/BannerModalClubeDeVantagens.webp';

import {
  Container,
  ModalCard,
  Header,
  Title,
  Subtitle,
  Banner,
  CloseButton,
  CloseIcon,
  StepHeader,
  StepItem,
  StepCircle,
  StepNumber,
  StepLabel,
  StepDivider,
  FormSpacing,
  ErrorMessage,
  CouponLink,
  CouponLinkText,
} from './styles';

type Step = 1 | 2;

type ValidationResponse =
  | boolean
  | {
    ok: boolean;
    message?: string;
    complete?: boolean;
    codigo?: string;
  };

interface ClubeAcessoModalProps {
  isVisible: boolean;
  onDismiss?: () => void;
  onSuccess?: (cupom: string, codigoPessoal: string) => void;
  validateCupom?: (cupom: string) => Promise<ValidationResponse> | ValidationResponse;
  validateCodigoPessoal?: (
    codigoPessoal: string,
    cupom: string,
  ) => Promise<ValidationResponse> | ValidationResponse;
  codigoLabel?: string;
  codigoPlaceholder?: string;
  stepTwoSubtitle?: string;
  codigoMask?: string;
  codigoMaskAffineFormats?: string[];
  onCodigoChange?: (formatted: string, extracted?: string) => string | void;
}

export function ClubeAcessoModal({
  isVisible,
  onDismiss,
  onSuccess,
  validateCupom,
  validateCodigoPessoal,
  codigoLabel = 'Código pessoal',
  codigoPlaceholder = 'Informe seu código',
  stepTwoSubtitle,
  codigoMask,
  codigoMaskAffineFormats,
  onCodigoChange,
}: ClubeAcessoModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [cupom, setCupom] = useState('');
  const [codigoPessoal, setCodigoPessoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const stepAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(stepAnimation, {
      toValue: step === 1 ? 0 : 1,
      duration: 300,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [step, stepAnimation]);

  useEffect(() => {
    if (!isVisible) {
      resetState();
    }
  }, [isVisible]);

  function resetState() {
    setStep(1);
    setCupom('');
    setCodigoPessoal('');
    setErrorMessage('');
    setLoading(false);
    stepAnimation.stopAnimation();
    stepAnimation.setValue(0);
  }

  function closeModal() {
    resetState();
    onDismiss?.();
  }

  function handleCodigoChange(formatted: string, extracted?: string) {
    const externalValue = onCodigoChange?.(formatted, extracted);

    if (typeof externalValue === 'string') {
      setCodigoPessoal(externalValue);
      return;
    }

    setCodigoPessoal(extracted ?? formatted);
  }

  function goToNextStep() {
    setStep(2);
  }

  function normalizeResult(result?: ValidationResponse) {
    if (typeof result === 'boolean') {
      return { ok: result };
    }

    if (result && typeof result === 'object') {
      return {
        ok: !!result.ok,
        message: result.message,
        complete: result.complete,
        codigo: result.codigo,
      };
    }

    return { ok: true };
  }

  async function handleValidateCupom() {
    Keyboard.dismiss();
    setLoading(true);
    setErrorMessage('');

    try {
      const trimmed = cupom.trim();

      const externalResponse = validateCupom
        ? await validateCupom(trimmed)
        : trimmed.length >= 3;

      const { ok, message, complete, codigo } =
        normalizeResult(externalResponse);

      if (!ok || trimmed.length < 3) {
        throw new Error(message || 'Cupom inválido. Confirme o código com o seu clube.');
      }

      if (complete) {
        onSuccess?.(trimmed, codigo || '');
        closeModal();
        return;
      }

      goToNextStep();
    } catch (error: any) {
      setErrorMessage(
        error?.message || 'Não conseguimos validar o cupom. Tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleValidateCodigoPessoal() {
    Keyboard.dismiss();
    setLoading(true);
    setErrorMessage('');

    try {
      const trimmedCodigo = codigoPessoal.trim();
      const trimmedCupom = cupom.trim();

      const externalResponse = validateCodigoPessoal
        ? await validateCodigoPessoal(trimmedCodigo, trimmedCupom)
        : trimmedCodigo.length >= 3;

      const { ok, message } = normalizeResult(externalResponse);

      if (!ok || trimmedCodigo.length < 3) {
        throw new Error(
          message || 'Código pessoal não encontrado. Revise os dados informados.',
        );
      }

      onSuccess?.(trimmedCupom, trimmedCodigo);
      closeModal();
    } catch (error: any) {
      setErrorMessage(
        error?.message ||
        'Não foi possível confirmar seu código pessoal. Tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  }

  const subtitleText =
    step === 1
      ? 'Insira o código/cupom de desconto exclusivo recebido da sua empresa parceira do Baratão, responsável por oferecer esse benefício aos seus usuários. \n\nEsse código libera imediatamente o acesso às vantagens exclusivas do Clube de Benefícios.'
      : stepTwoSubtitle ||
      `Cupom válido! Agora informe ${codigoLabel.toLowerCase()} para liberar o acesso às vantagens.`;

  const stepOneAnimatedStyle = {
    opacity: stepAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 0],
    }),
    transform: [
      {
        translateX: stepAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -30],
        }),
      },
    ],
  };

  const stepTwoAnimatedStyle = {
    opacity: stepAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
    transform: [
      {
        translateX: stepAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [30, 0],
        }),
      },
    ],
  };

  return (
    <Container>
      <Modal
        isVisible={isVisible}
        onDismiss={closeModal}
        onBackdropPress={closeModal}
        onBackButtonPress={closeModal}
        useNativeDriver
        backdropOpacity={0.35}
        style={{ margin: 0, justifyContent: 'center', paddingHorizontal: 18 }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 24 : 0}>
          <ModalCard>
            <Header>
              <Title>Clube de Vantagens</Title>
              <CloseButton activeOpacity={0.9} onPress={closeModal}>
                <CloseIcon name="x" />
              </CloseButton>
            </Header>
            <Banner source={BannerModal} resizeMode="contain" />
            <Subtitle>{subtitleText}</Subtitle>

            <StepHeader>
              <StepItem>
                <StepCircle active={step === 1}>
                  <StepNumber active={step === 1}>1</StepNumber>
                </StepCircle>
                <StepLabel active={step === 1}>Cupom</StepLabel>
              </StepItem>

              <StepDivider />

              <StepItem>
                <StepCircle active={step === 2}>
                  <StepNumber active={step === 2}>2</StepNumber>
                </StepCircle>
                <StepLabel active={step === 2}>{codigoLabel}</StepLabel>
              </StepItem>
            </StepHeader>

            <FormSpacing
              style={{
                minHeight: RFValue(errorMessage ? 190 : 170),
                paddingBottom: RFValue(errorMessage ? 20 : 12),
              }}>

              <Animated.View
                style={[
                  {
                    width: '100%',
                    position: 'absolute',
                  },
                  stepOneAnimatedStyle,
                ]}
                pointerEvents={step === 1 ? 'auto' : 'none'}
              >
                <Input
                  labelText="Código do cupom"
                  value={cupom}
                  onChangeText={setCupom}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  placeholder="Ex.: CLUBE2024"
                  returnKeyType="done"
                />

                {!!errorMessage && step === 1 && (
                  <ErrorMessage>{errorMessage}</ErrorMessage>
                )}

                <Button
                  title="Continuar"
                  enabled={!!cupom.trim() && !loading}
                  loading={loading}
                  onPress={handleValidateCupom}
                  mt={16}
                />

                <CouponLink
                  activeOpacity={1}
                  onPress={() => {
                    Linking.openURL(
                      'https://api.whatsapp.com/send/?phone=5561998202004&text=Ol%C3%A1%2C+gostaria+de+saber+mais+sobre+o+clube+de+vantagens.&type=phone_number&app_absent=0'
                    );
                  }}
                >
                  <CouponLinkText>
                    Não tem cupom? Clique aqui e fale conosco!
                  </CouponLinkText>
                </CouponLink>
              </Animated.View>

              <Animated.View
                style={[
                  {
                    width: '100%',
                    position: 'absolute',
                  },
                  stepTwoAnimatedStyle,
                ]}
                pointerEvents={step === 2 ? 'auto' : 'none'}>
                <Input
                  labelText={codigoLabel}
                  value={codigoPessoal}
                  onChangeText={handleCodigoChange}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  placeholder={codigoPlaceholder}
                  returnKeyType="done"
                  mask={codigoMask}
                  affineFormats={codigoMaskAffineFormats}
                />

                <Button
                  title="Confirmar e Entrar"
                  enabled={!!codigoPessoal.trim() && !loading}
                  loading={loading}
                  onPress={handleValidateCodigoPessoal}
                  mt={16}
                />

                {!!errorMessage && step === 2 && (
                  <ErrorMessage>{errorMessage}</ErrorMessage>
                )}
              </Animated.View>
            </FormSpacing>

          </ModalCard>
        </KeyboardAvoidingView>
      </Modal>
    </Container>
  );
}
